<template>
  <div class="watchers-picker">
    <div v-if="!hideInstructions" class="watchers-picker__instructions">{{instructions}}</div>
    <div class="watchers-picker__content">
      <div class="watchers-picker__current-user marker--watchers-picker--current-user">
        <dd-button
          v-if="isCurrentUserSelected"
          size="small"
          type="flat"
          color="grey"
          @click="handleSelection(currentUserId, true)"
        >
          <span class="text">You</span>
          <i class="ddi ddi-check" />
        </dd-button>
        <dd-button v-else size="small" type="flat" color="grey" @click="handleSelection(currentUserId, true)">
          <span class="text">You</span>
        </dd-button>
      </div>
      <dropdown
        v-if="!isDisabled"
        ref="dropdownRef"
        title="Assignees" class="watchers-picker__add"
        @open="handleDropdownOpen()"
      >
        <dd-button size="small" type="flat" color="grey">
          <span class="text">Others&hellip;</span>
        </dd-button>
        <template #content>
          <div>
            <sleek-filter-input
              v-if="showFilterInput"
              ref="sleekFilterInputRef"
              v-model="filterQuery"
              placeholder="Search watcher by name…"
              show-small-text
            />
            <ul>
              <li @click="handleSelection(currentUser.id)">
                <avatar :size="20" :person="currentUser" />
                <span :class="{'selected': isSelected(currentUser.id)}">
                  <span>You ({{ currentUser.name }})</span>
                </span>
              </li>
            </ul>
            <hr>
            <ul>
              <li
                v-for="watcher in filteredOptions"
                :key="watcher.id"
                @click="handleSelection(watcher.id)"
              >
                <avatar :size="20" :person="watcher" />
                <span :class="{'selected': isSelected(watcher.id)}">
                  {{ watcher.name }}
                  <i v-if="watcher.isGuest" class="ddi ddi-warning" title="Guest user" />
                </span>
              </li>
            </ul>
          </div>
        </template>
      </dropdown>
      <div v-if="hasWatchers" class="current-watchers">
        <avatar-gallery
          :size="24"
          :people="selectedWatchers"
          :max-num-avatars="maxNumberAvatars"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */

export default defineComponent({
  name: 'WatchersPicker',

  extends: SearchableDropdownMixin,

  props: {
    options: {
      type: Array as () => IPersonWithEmail[],
      default: () => [] as IPersonWithEmail[],
    },
    currentUserId: {
      type: Number,
      default: null,
    },
    type: {
      type: String as () => EEntities,
      default: EEntities.MAILBOXES,
    },
    hideInstructions: {
      type: Boolean,
      default: false,
    },
    fromDrawer: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showWatchersList: false,

      /* SearchableDropdownMixin configuration */
      allowMultipleSelections: true,
    }
  },

  computed: {
    currentUser(): IPersonWithEmail {
      return this.options.find(p => p.id === this.currentUserId)
    },
    totalOptionsCount(): number {
      return this.options.length
    },
    filteredOptions(): IPersonWithEmail[] {
      const result = this.options.filter(p => p.id !== this.currentUserId)

      if (this.filterQuery.length === 0) {
        return result
      }

      return result.filter(person => {
        return person.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    hasWatchers(): boolean {
      return this.selectedWatchers.length > 0
    },
    selectedWatchers(): IPersonWithEmail[] {
      return this.options.filter(o => this.internalSelectedIDs.includes(o.id))
    },
    isCurrentUserSelected(): boolean {
      return this.isSelected(this.currentUserId)
    },
    getTypeString(): string {
      return TextAssistant.itemSingularName(this.type).toLowerCase()
    },
    instructions(): string {
      if (!this.hasWatchers) {
        return `Add watchers to this ${this.getTypeString}...`
      }

      if (this.selectedWatchers.length === 1) {
        if (this.isCurrentUserSelected) {
          return `You are watching this ${this.getTypeString}`
        }

        return `${this.selectedWatchers[0].name} is watching this ${
          this.getTypeString
        }`
      }

      return `${this.selectedWatchers.length} people are watching this ${
        this.getTypeString
      }`
    },
    maxNumberAvatars(): number {
      return this.$screen.isMobile(this.$mq) || this.fromDrawer ? 6 : 10
    },
  },
})
</script>


<style lang="scss">
.watchers-picker {
  display: flex;
  flex-direction: column;

  &__instructions {
    margin-bottom: 14px;
    @include lettering--p-sm--no-color;
    color: var(--color-text-third);
  }

  &__content {
    display: flex;
    @include flex-row-vertical-spacing-on-wrap(8px);
  }

  &__current-user {
    display: flex;
    flex: none;
    align-items: center;

    width: auto;
    height: 100%;
    margin-right: 8px;

    .ddi-check {
      padding-left: 4px;
      font-size: 10px;
    }
  }

  &__add {
    margin-right: 12px;
  }

  .el-popover__reference {
    height: 100%;
  }

  .el-button {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 2px 8px;
    background-color: var(--color-background-secondary);
    border: solid 1px $light-blue-grey-two;

    .text {
      display: flex;
      align-items: center;
    }
  }

  .current-watchers {
    display: flex;
    align-items: center;
  }

  .ddi-warning {
    color: $color--orange-40 !important;
  }
}

.el-popover.el-popper.watchers-picker__popover {
  padding: 0;
  @include media-breakpoint-up(sm) {
    transform: translateY(-5px);
  }
}
</style>
